import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input, Label, Select, Textarea } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useToast } from "@/app/Toast";
import { formatMoney } from "@/lib/utils";

interface ServiceRow { id: number; name: string; durationMinutes: number; priceCents: number; active?: boolean; }
interface ResourceRow { id: number; name: string; type: string; active?: boolean; staffUserId?: number | null; defaultStaffUserIds?: number[]; }
interface LocationRow { id: number; name: string; timezone: string; currency: string; active?: boolean; }
interface AvailabilitySlot { startAt: string; endAt: string; resourceIds: number[]; }
interface AddonRow {
  id: number; name: string; code: string; priceCents: number; impactMinutes: number; serviceId: number;
}
interface CustomBookingItem { id: string; name: string; durationMinutes: number; priceCents: number; }
interface CustomerSearchRow { id: number; firstName: string; lastName: string; email: string | null; phone: string | null; }
interface VehicleRow { id: number; plate: string; make: string | null; model: string | null; color: string | null; vehicleType: string | null; archivedAt: string | null; }
interface CustomerMembership { id: number; name: string; status: string; benefits: unknown; }
const CUSTOM_TEMPLATES = [
  { name: "Monthly subscription wash", durationMinutes: 60, priceCents: 10000 },
  { name: "Interior detailing", durationMinutes: 90, priceCents: 15000 },
  { name: "Exterior polishing", durationMinutes: 90, priceCents: 18000 },
  { name: "Engine bay cleaning", durationMinutes: 45, priceCents: 8000 },
  { name: "Seat cleaning", durationMinutes: 60, priceCents: 10000 },
  { name: "Interior and exterior full service", durationMinutes: 150, priceCents: 25000 },
];
type ServiceMode = "STATIONARY" | "MOBILE";

export function NewBookingPage() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const query = useMemo(() => parseBookingQuery(searchParams), [searchParams]);
  const requestedTime = useRef(query.time);
  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [resources, setResources] = useState<ResourceRow[]>([]);
  const [services, setServices] = useState<ServiceRow[]>([]);
  const [addons, setAddons] = useState<AddonRow[]>([]);
  const [locationId, setLocationId] = useState<number | null>(query.locationId);
  const [resourceId, setResourceId] = useState<number | null>(query.resourceId);
  const [staffResourceIds, setStaffResourceIds] = useState<Set<number>>(() => new Set(query.staffResourceIds));
  const [serviceMode, setServiceMode] = useState<ServiceMode>(query.serviceMode);
  const [serviceIds, setServiceIds] = useState<Set<number>>(() => new Set(query.serviceIds));
  const [addonIds, setAddonIds] = useState<Set<number>>(() => new Set(query.addonIds));
  const [customItems, setCustomItems] = useState<CustomBookingItem[]>([]);
  const [date, setDate] = useState(() => query.date ?? new Date().toISOString().slice(0, 10));
  const [slots, setSlots] = useState<AvailabilitySlot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [startAt, setStartAt] = useState<string>("");
  const [customer, setCustomer] = useState(query.customer);
  const [customerMode, setCustomerMode] = useState<"existing" | "new">(query.customerId ? "existing" : "new");
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(query.customerId);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerResults, setCustomerResults] = useState<CustomerSearchRow[]>([]);
  const [possibleDuplicates, setPossibleDuplicates] = useState<CustomerSearchRow[]>([]);
  const [vehicles, setVehicles] = useState<VehicleRow[]>([]);
  const [vehicleMode, setVehicleMode] = useState<"existing" | "new">("new");
  const [selectedVehicleId, setSelectedVehicleId] = useState<number | null>(null);
  const [customerMemberships, setCustomerMemberships] = useState<CustomerMembership[]>([]);
  const [vehicle, setVehicle] = useState({ plate: "", make: "", model: "" });
  const [serviceAddress, setServiceAddress] = useState(query.serviceAddress);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [idemKey, setIdemKey] = useState(() => crypto.randomUUID());

  useEffect(() => {
    api.get<LocationRow[]>("/api/v1/locations").then((l) => {
      const active = l.filter((row) => row.active !== false);
      setLocations(active);
      setLocationId((current) => active.some((row) => row.id === current) ? current : active[0]?.id ?? null);
    }).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!locationId) return;
    Promise.all([
      api.get<ServiceRow[]>(`/api/v1/ops/services?locationId=${locationId}`),
      api.get<ResourceRow[]>(`/api/v1/ops/resources?locationId=${locationId}`),
    ]).then(([svc, res]) => {
      const activeServices = svc.filter((row) => row.active !== false);
      const activeResources = res.filter((row) => row.active !== false);
      setServices(activeServices);
      setResources(activeResources);
      setServiceIds((current) => new Set(Array.from(current).filter((id) => activeServices.some((row) => row.id === id))));
      // Pick the first resource that matches the mode.
      const filtered = activeResources.filter((r) =>
        serviceMode === "MOBILE" ? r.type === "STAFF_MEMBER" : r.type !== "STAFF_MEMBER",
      );
      setResourceId((current) => filtered.some((row) => row.id === current) ? current : filtered[0]?.id ?? null);
       const defaultStaffResourceIds = (filtered[0]?.defaultStaffUserIds ?? [])
         .map((userId) => activeResources.find((resource) => resource.staffUserId === userId)?.id)
         .filter((id): id is number => id != null);
       setStaffResourceIds((current) => current.size > 0 ? new Set(Array.from(current).filter((id) => activeResources.some((row) => row.id === id && row.type === "STAFF_MEMBER"))) : new Set(defaultStaffResourceIds));
    }).catch(() => undefined);
  }, [locationId, serviceMode]);

  useEffect(() => {
    if (!locationId) { setSlots([]); return; }
    const location = locations.find((l) => l.id === locationId);
    if (!location) return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      const params = new URLSearchParams({
        locationId: String(locationId),
        serviceIds: Array.from(serviceIds).join(","),
        date,
        timezone: location.timezone,
        serviceMode,
      });
      if (resourceId) params.set("resourceId", String(resourceId));
      if (staffResourceIds.size) params.set("staffResourceIds", Array.from(staffResourceIds).join(","));
      if (addonIds.size) params.set("addonIds", Array.from(addonIds).join(","));
      const customDurationMinutes = customItems.reduce((sum, item) => sum + item.durationMinutes, 0);
      if (customDurationMinutes) params.set("customDurationMinutes", String(customDurationMinutes));
      if (selectedCustomerId) params.set("customerId", String(selectedCustomerId));
      // Do not geocode or calculate travel against a half-entered address.
      const addressReady = serviceAddress.line1.trim() && serviceAddress.city?.trim() && serviceAddress.countryCode?.trim().length === 2;
      if (serviceMode === "MOBILE" && addressReady) {
        params.set("serviceAddressLine1", serviceAddress.line1);
        if (serviceAddress.line2) params.set("serviceAddressLine2", serviceAddress.line2);
        if (serviceAddress.city) params.set("serviceCity", serviceAddress.city);
        if (serviceAddress.region) params.set("serviceRegion", serviceAddress.region);
        if (serviceAddress.postalCode) params.set("servicePostalCode", serviceAddress.postalCode);
        if (serviceAddress.countryCode) params.set("serviceCountryCode", serviceAddress.countryCode);
      }
      setSlotsLoading(true);
      api.get<AvailabilitySlot[]>(`/api/v1/bookings/availability?${params.toString()}`, controller.signal)
        .then((nextSlots) => {
          setSlots(nextSlots);
          const requested = requestedTime.current;
          if (requested) {
            const match = nextSlots.find((slot) => slotMatchesTime(slot.startAt, requested, date, location.timezone));
            if (match) setStartAt(match.startAt);
            requestedTime.current = null;
          }
        }).catch((e) => { if (e.name !== "AbortError") toast("error", e.message); })
        .finally(() => setSlotsLoading(false));
    }, 250);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [locationId, serviceIds, addonIds, customItems, resourceId, staffResourceIds, selectedCustomerId, date, locations, serviceMode, serviceAddress, toast]);

  useEffect(() => {
    setStartAt("");
  }, [date, locationId, serviceMode, resourceId, staffResourceIds, serviceIds, addonIds, serviceAddress]);

  // Add-ons: only those attached to currently-selected services.
  useEffect(() => {
    if (!locationId || serviceIds.size === 0) { setAddons([]); return; }
    api.get<AddonRow[]>(`/api/v1/ops/addons?locationId=${locationId}`)
      .then((rows) => setAddons(rows.filter((a) => serviceIds.has(a.serviceId))))
      .catch(() => setAddons([]));
  }, [locationId, serviceIds]);

  useEffect(() => {
    if (!selectedCustomerId) {
      setVehicles([]);
      setSelectedVehicleId(null);
      setVehicleMode("new");
      return;
    }
    api.get<{ firstName: string; lastName: string; email: string | null; phone: string | null; vehicles: VehicleRow[] }>(`/api/v1/customers/${selectedCustomerId}`)
      .then((row) => {
        setCustomer({ firstName: row.firstName, lastName: row.lastName, email: row.email ?? "", phone: row.phone ?? "" });
        const activeVehicles = (row.vehicles ?? []).filter((vehicle) => !vehicle.archivedAt);
        setVehicles(activeVehicles);
        setSelectedVehicleId(null);
        setVehicleMode(activeVehicles.length > 0 ? "existing" : "new");
      })
      .catch((error) => toast("error", error instanceof Error ? error.message : "Could not load customer"));
  }, [selectedCustomerId, toast]);

  useEffect(() => {
    if (!selectedCustomerId || !locationId) {
      setCustomerMemberships([]);
      return;
    }
    api.get<{ customerMemberships: CustomerMembership[] }>(`/api/v1/customer-value/overview?locationId=${locationId}&customerId=${selectedCustomerId}`)
      .then((overview) => setCustomerMemberships(overview.customerMemberships ?? []))
      .catch(() => setCustomerMemberships([]));
  }, [locationId, selectedCustomerId]);

  useEffect(() => {
    if (customerMode !== "existing" || customerSearch.trim().length < 2) {
      setCustomerResults([]);
      return;
    }
    api.get<CustomerSearchRow[]>(`/api/v1/customers?q=${encodeURIComponent(customerSearch.trim())}`)
      .then(setCustomerResults)
      .catch(() => setCustomerResults([]));
  }, [customerMode, customerSearch]);

  useEffect(() => {
    if (customerMode !== "new") {
      setPossibleDuplicates([]);
      return;
    }
    const lookup = customer.email.trim() || customer.phone.trim();
    if (lookup.length < 4) {
      setPossibleDuplicates([]);
      return;
    }
    api.get<CustomerSearchRow[]>(`/api/v1/customers?q=${encodeURIComponent(lookup)}`)
      .then((rows) => setPossibleDuplicates(rows.slice(0, 3)))
      .catch(() => setPossibleDuplicates([]));
  }, [customerMode, customer.email, customer.phone]);

  const filteredResources = useMemo(() => {
    if (serviceMode === "MOBILE") return resources.filter((r) => r.type === "STAFF_MEMBER");
    return resources.filter((r) => r.type !== "STAFF_MEMBER");
  }, [resources, serviceMode]);
  const staffResources = useMemo(
    () => resources.filter((r) => r.type === "STAFF_MEMBER" && r.id !== resourceId),
    [resources, resourceId],
  );

  const currency = locations.find((l) => l.id === locationId)?.currency ?? "USD";
  const servicesSubtotal = services.filter((s) => serviceIds.has(s.id)).reduce((s, r) => s + r.priceCents, 0);
  const addonsSubtotal = addons.filter((a) => addonIds.has(a.id)).reduce((s, a) => s + a.priceCents, 0);
  const customSubtotal = customItems.reduce((sum, item) => sum + item.priceCents, 0);
  const total = servicesSubtotal + addonsSubtotal + customSubtotal;
  const totalMinutes = services.filter((s) => serviceIds.has(s.id)).reduce((s, r) => s + r.durationMinutes, 0)
    + addons.filter((a) => addonIds.has(a.id)).reduce((s, a) => s + a.impactMinutes, 0)
    + customItems.reduce((sum, item) => sum + item.durationMinutes, 0);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (serviceIds.size === 0 && customItems.length === 0) {
      toast("error", "Select at least one service or add a custom item.");
      return;
    }
    if (!resourceId) {
      toast("error", "Select a bay or staff member.");
      return;
    }
    if (customerMode === "existing" && !selectedCustomerId) {
      toast("error", "Select an existing customer or choose New customer.");
      return;
    }
    if (serviceMode === "MOBILE" && (!serviceAddress.line1.trim() || !serviceAddress.city?.trim() || serviceAddress.countryCode?.trim().length !== 2)) {
      toast("error", "Complete the mobile address, including city and 2-letter country code.");
      return;
    }
    if (!startAt || !slots.some((slot) => slot.startAt === startAt)) {
      setStartAt("");
      toast("error", "That time is no longer available. Please select another slot.");
      return;
    }
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        locationId,
        serviceIds: Array.from(serviceIds),
        startAt,
        serviceMode,
        customer,
      };
      if (selectedCustomerId) body.customerId = selectedCustomerId;
      if (selectedVehicleId) body.vehicleId = selectedVehicleId;
      if (resourceId) body.resourceId = resourceId;
      if (staffResourceIds.size > 0) body.staffResourceIds = Array.from(staffResourceIds);
      if (addonIds.size > 0) body.addonIds = Array.from(addonIds);
      if (customItems.length > 0) body.customItems = customItems.map(({ name, durationMinutes, priceCents }) => ({ name, durationMinutes, priceCents }));
       if (!selectedVehicleId && vehicle.plate) body.vehicle = vehicle;
      if (serviceMode === "MOBILE") {
        body.serviceAddress = serviceAddress;
      }
      if (notes) body.notes = notes;
      const res = await api.post<{ id: number; code: string }>("/api/v1/bookings", body, true, { "Idempotency-Key": idemKey });
      toast("success", `Booking ${res.code} created${serviceMode === "MOBILE" ? " (mobile)" : ""}`);
      nav(`/bookings`);
      void res;
    } catch (err) {
      setIdemKey(crypto.randomUUID());
      const code = (err as { code?: string }).code;
      const message = err instanceof Error ? err.message : "Booking failed";
      if (code === "SLOT_TAKEN") {
        toast("error", message);
      } else {
        toast("error", message);
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (locations.length === 0) {
    return <p className="text-sm text-muted-foreground">Loading locations…</p>;
  }

  return (
    <form onSubmit={submit} className="space-y-4">
       <div>
         <h1 className="text-2xl font-semibold tracking-tight">New booking</h1>
         <p className="mt-1 text-xs text-muted-foreground"><span className="font-medium text-foreground">Required</span> fields are needed to book. <span className="ml-2">Optional fields improve the customer record.</span></p>
       </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Service mode</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3 sm:flex-row">
            <label className="flex flex-1 cursor-pointer items-center gap-3 rounded-md border bg-muted/30 px-3 py-3 text-sm has-[:checked]:border-primary has-[:checked]:bg-primary/10">
              <input type="radio" name="mode" value="STATIONARY" checked={serviceMode === "STATIONARY"} onChange={() => setServiceMode("STATIONARY")} />
              <div>
                <div className="font-medium">In-bay</div>
                <div className="text-xs text-muted-foreground">Customer brings the car to the shop.</div>
              </div>
            </label>
            <label className="flex flex-1 cursor-pointer items-center gap-3 rounded-md border bg-muted/30 px-3 py-3 text-sm has-[:checked]:border-primary has-[:checked]:bg-primary/10">
              <input type="radio" name="mode" value="MOBILE" checked={serviceMode === "MOBILE"} onChange={() => setServiceMode("MOBILE")} />
              <div>
                <div className="font-medium">Mobile</div>
                <div className="text-xs text-muted-foreground">We dispatch a team to the customer. Travel time is auto-computed.</div>
              </div>
            </label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">When</CardTitle></CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <Field>
             <FieldLabel>Location</FieldLabel>
            <Select value={locationId?.toString() ?? ""} onChange={(e) => setLocationId(Number(e.target.value))}>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </Select>
          </Field>
          <Field>
             <FieldLabel>{serviceMode === "MOBILE" ? "Team / staff member" : "Bay / resource"}</FieldLabel>
            <Select
              value={resourceId?.toString() ?? ""}
              onChange={(e) => {
                const next = Number(e.target.value);
               setResourceId(next);
                 const selected = filteredResources.find((resource) => resource.id === next);
                 const defaultStaffResourceIds = (selected?.defaultStaffUserIds ?? [])
                   .map((userId) => resources.find((resource) => resource.staffUserId === userId)?.id)
                   .filter((id): id is number => id != null);
                 setStaffResourceIds(new Set(defaultStaffResourceIds));
              }}
              disabled={filteredResources.length === 0}
            >
              {filteredResources.length === 0 && <option value="">No {serviceMode === "MOBILE" ? "staff members" : "bays"} configured</option>}
              {filteredResources.map((r) => <option key={r.id} value={r.id}>{r.name} · {r.type}</option>)}
            </Select>
          </Field>
          <div className="md:col-span-2 space-y-2">
            <Label className="block">{serviceMode === "MOBILE" ? "Additional team members" : "Assigned staff"}</Label>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {staffResources.map((resource) => (
                <label key={resource.id} className="flex items-center gap-2 rounded border bg-muted/20 px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    checked={staffResourceIds.has(resource.id)}
                    onChange={(event) => {
                      const next = new Set(staffResourceIds);
                      if (event.currentTarget.checked) next.add(resource.id); else next.delete(resource.id);
                      setStaffResourceIds(next);
                    }}
                  />
                  <span>{resource.name}</span>
                </label>
              ))}
              {staffResources.length === 0 && <p className="text-xs text-muted-foreground">No additional staff resources configured.</p>}
            </div>
          </div>
          <Field>
             <FieldLabel>Date</FieldLabel>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
          <Field>
               <FieldLabel>Available starts</FieldLabel>
              {serviceIds.size === 0 && <p className="text-xs text-muted-foreground">Choose a service to refine slot duration.</p>}
             <Select required value={startAt} disabled={slotsLoading} onChange={(e) => setStartAt(e.target.value)}>
               <option value="">Select a slot…</option>
              {slots.map((s) => (
                <option key={s.startAt} value={s.startAt}>
                  {formatSlotTime(s.startAt, locations.find((l) => l.id === locationId)?.timezone)}
                </option>
               ))}
             </Select>
             {slotsLoading && <p className="text-xs text-muted-foreground">Updating available times…</p>}
             {!slotsLoading && slots.length === 0 && <p className="text-xs text-muted-foreground">No available times for this date and resource.</p>}
          </Field>
          <div className="md:col-span-2 space-y-2">
             <FieldLabel className="mb-1 block">Services</FieldLabel>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {services.map((s) => (
                <label key={s.id} className="flex items-center gap-2 rounded border bg-muted/30 px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={serviceIds.has(s.id)}
                    onChange={(e) => {
                      const n = new Set(serviceIds);
                      if (e.currentTarget.checked) n.add(s.id); else n.delete(s.id);
                      setServiceIds(n);
                      // Cascade: remove any addons that no longer have a parent in serviceIds
                      setAddonIds((curr) => new Set(Array.from(curr).filter((id) => n.has(addons.find((a) => a.id === id)?.serviceId ?? -1))));
                    }}
                  />
                  <span className="flex-1">{s.name}</span>
                  <span className="text-xs text-muted-foreground">{s.durationMinutes}m · {formatMoney(s.priceCents, currency)}</span>
                </label>
              ))}
              {services.length === 0 && <p className="text-xs text-muted-foreground">No services at this location.</p>}
            </div>
          </div>

          {/* Add-ons — only show when at least one service is selected */}
          {serviceIds.size > 0 && (
            <div className="md:col-span-2 space-y-2">
              <Label className="mb-1 block">
                Add-ons
                <span className="ml-2 text-xs font-normal text-muted-foreground">Optional extras with each base service</span>
              </Label>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {addons.length === 0 && (
                  <p className="col-span-full text-xs text-muted-foreground">
                    No add-ons configured for the selected services. Add them in Settings → Add-ons.
                  </p>
                )}
                {addons.map((a) => (
                  <label key={a.id} className="flex items-center gap-2 rounded border bg-muted/20 px-3 py-2 text-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={addonIds.has(a.id)}
                      onChange={(e) => {
                        const n = new Set(addonIds);
                        if (e.currentTarget.checked) n.add(a.id); else n.delete(a.id);
                        setAddonIds(n);
                      }}
                    />
                    <span className="flex-1">{a.name}</span>
                    <span className="text-xs text-muted-foreground">
                      +{formatMoney(a.priceCents, currency)}{a.impactMinutes > 0 ? ` · +${a.impactMinutes}m` : ""}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="md:col-span-2 space-y-2 border-t pt-4">
             <FieldLabel optional className="mb-1 block">Custom line items</FieldLabel>
            <p className="text-xs text-muted-foreground">Add a one-off service for this booking. The name, time, and price can be edited without changing the catalog.</p>
            <Select aria-label="Add custom line item" value="" onChange={(event) => {
              const template = CUSTOM_TEMPLATES.find((item) => item.name === event.target.value);
              if (template) setCustomItems((items) => [...items, { ...template, id: crypto.randomUUID() }]);
            }}>
              <option value="">Add a custom item…</option>
              {CUSTOM_TEMPLATES.map((item) => <option key={item.name} value={item.name}>{item.name} · {item.durationMinutes}m · {formatMoney(item.priceCents, currency)}</option>)}
            </Select>
            {customItems.map((item) => (
              <div key={item.id} className="grid gap-2 rounded border p-3 sm:grid-cols-[minmax(0,1fr)_7rem_8rem_auto] sm:items-end">
                <Field><Label>Name</Label><Input value={item.name} onChange={(event) => setCustomItems((items) => items.map((current) => current.id === item.id ? { ...current, name: event.target.value } : current))} /></Field>
                <Field><Label>Minutes</Label><Input type="number" min={1} max={720} value={item.durationMinutes} onChange={(event) => setCustomItems((items) => items.map((current) => current.id === item.id ? { ...current, durationMinutes: Number(event.target.value) } : current))} /></Field>
                <Field><Label>Price ({currency})</Label><Input type="number" min={0} step="0.01" value={(item.priceCents / 100).toFixed(2)} onChange={(event) => setCustomItems((items) => items.map((current) => current.id === item.id ? { ...current, priceCents: Math.round(Number(event.target.value) * 100) } : current))} /></Field>
                <Button type="button" variant="ghost" onClick={() => setCustomItems((items) => items.filter((current) => current.id !== item.id))}>Remove</Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {serviceMode === "MOBILE" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Service address</CardTitle>
            <p className="text-xs text-muted-foreground">Where the team will be working. Travel time is auto-computed via Google Distance Matrix.</p>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <Field className="md:col-span-2">
               <FieldLabel>Address line 1</FieldLabel>
              <Input required value={serviceAddress.line1} onChange={(e) => setServiceAddress({ ...serviceAddress, line1: e.target.value })} placeholder="Street, building, apartment" />
            </Field>
            <Field>
               <FieldLabel optional>Address line 2</FieldLabel>
              <Input value={serviceAddress.line2 ?? ""} onChange={(e) => setServiceAddress({ ...serviceAddress, line2: e.target.value })} />
            </Field>
            <Field>
               <FieldLabel>City</FieldLabel>
              <Input value={serviceAddress.city ?? ""} onChange={(e) => setServiceAddress({ ...serviceAddress, city: e.target.value })} />
            </Field>
            <Field>
               <FieldLabel optional>Region / state</FieldLabel>
              <Input value={serviceAddress.region ?? ""} onChange={(e) => setServiceAddress({ ...serviceAddress, region: e.target.value })} />
            </Field>
            <Field>
               <FieldLabel optional>Postal code</FieldLabel>
              <Input value={serviceAddress.postalCode ?? ""} onChange={(e) => setServiceAddress({ ...serviceAddress, postalCode: e.target.value })} />
            </Field>
            <Field>
               <FieldLabel>Country (ISO 3166-1 alpha-2)</FieldLabel>
              <Input maxLength={2} value={serviceAddress.countryCode ?? ""} onChange={(e) => setServiceAddress({ ...serviceAddress, countryCode: e.target.value.toUpperCase() })} placeholder="LK" />
            </Field>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Customer</CardTitle>
          <div className="flex gap-2 pt-2">
            <Button type="button" variant={customerMode === "existing" ? "default" : "outline"} onClick={() => setCustomerMode("existing")}>Existing customer</Button>
            <Button type="button" variant={customerMode === "new" ? "default" : "outline"} onClick={() => { setCustomerMode("new"); setSelectedCustomerId(null); }}>New customer</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {customerMode === "existing" ? (
            <div className="space-y-3">
              <Field>
                <FieldLabel>Search customers</FieldLabel>
                <Input value={customerSearch} onChange={(e) => setCustomerSearch(e.target.value)} placeholder="Name, phone, or email" />
              </Field>
              {customerResults.length > 0 && (
                <div className="divide-y rounded-md border">
                  {customerResults.map((row) => (
                    <button
                      type="button"
                      key={row.id}
                      className="block w-full px-3 py-2 text-left text-sm hover:bg-muted"
                      onClick={() => {
                        setSelectedCustomerId(row.id);
                        setCustomer({ firstName: row.firstName, lastName: row.lastName, email: row.email ?? "", phone: row.phone ?? "" });
                        setSelectedVehicleId(null);
                        setCustomerSearch(`${row.firstName} ${row.lastName}`);
                        setCustomerResults([]);
                      }}
                    >
                      <span className="font-medium">{row.firstName} {row.lastName}</span>
                      <span className="ml-2 text-muted-foreground">{row.phone ?? row.email ?? "No contact details"}</span>
                    </button>
                  ))}
                </div>
              )}
              {selectedCustomerId ? (
                <div className="space-y-2 text-sm">
                  <p className="text-muted-foreground">Selected: <span className="font-medium text-foreground">{customer.firstName} {customer.lastName}</span></p>
                  {customerMemberships.length > 0 ? (
                    <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-emerald-950">
                      <p className="font-medium">Membership benefits</p>
                      {customerMemberships.map((membership) => <p key={membership.id} className="text-xs">{membership.name} · {membership.status}</p>)}
                      <p className="mt-1 text-xs">Eligible benefits will be applied when the booking is created.</p>
                    </div>
                  ) : <p className="text-xs text-muted-foreground">No active membership at this location.</p>}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Search and select a customer before creating the booking.</p>
              )}
            </div>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field><FieldLabel>First name</FieldLabel><Input required value={customer.firstName} onChange={(e) => setCustomer({ ...customer, firstName: e.target.value })} /></Field>
                <Field><FieldLabel>Last name</FieldLabel><Input required value={customer.lastName} onChange={(e) => setCustomer({ ...customer, lastName: e.target.value })} /></Field>
                <Field><FieldLabel optional>Email</FieldLabel><Input type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} /></Field>
                <Field><FieldLabel optional>Phone</FieldLabel><Input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} /></Field>
              </div>
              <p className="text-xs text-muted-foreground">This customer will be added to the CRM when the booking is created.</p>
              {possibleDuplicates.length > 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
                  <p className="font-medium">Possible existing customer</p>
                  {possibleDuplicates.map((row) => (
                    <button type="button" key={row.id} className="mt-1 block underline" onClick={() => {
                      setCustomerMode("existing");
                      setSelectedCustomerId(row.id);
                      setCustomer({ firstName: row.firstName, lastName: row.lastName, email: row.email ?? "", phone: row.phone ?? "" });
                      setPossibleDuplicates([]);
                    }}>
                      Use {row.firstName} {row.lastName} {row.phone ? `· ${row.phone}` : row.email ? `· ${row.email}` : ""}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Vehicle</CardTitle>
          {selectedCustomerId && (
            <div className="flex gap-2 pt-2">
              <Button type="button" size="sm" variant={vehicleMode === "existing" ? "default" : "outline"} onClick={() => setVehicleMode("existing")} disabled={vehicles.length === 0}>Saved vehicle</Button>
              <Button type="button" size="sm" variant={vehicleMode === "new" ? "default" : "outline"} onClick={() => { setVehicleMode("new"); setSelectedVehicleId(null); }}>Add new vehicle</Button>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {selectedCustomerId && vehicleMode === "existing" ? (
            <Field>
              <Label>Saved vehicle</Label>
              <Select required value={selectedVehicleId?.toString() ?? ""} onChange={(e) => setSelectedVehicleId(Number(e.target.value))}>
                <option value="" disabled>Select a vehicle</option>
                {vehicles.map((item) => <option key={item.id} value={item.id}>{item.plate} · {[item.make, item.model].filter(Boolean).join(" ") || "Vehicle"}</option>)}
              </Select>
            </Field>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field><FieldLabel optional={!selectedCustomerId}>Plate</FieldLabel><Input required={Boolean(selectedCustomerId && vehicleMode === "new")} value={vehicle.plate} onChange={(e) => setVehicle({ ...vehicle, plate: e.target.value })} /></Field>
              <Field><FieldLabel optional>Make</FieldLabel><Input value={vehicle.make} onChange={(e) => setVehicle({ ...vehicle, make: e.target.value })} /></Field>
              <Field><FieldLabel optional>Model</FieldLabel><Input value={vehicle.model} onChange={(e) => setVehicle({ ...vehicle, model: e.target.value })} /></Field>
            </div>
          )}
          <Field><FieldLabel optional>Notes</FieldLabel><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Summary</CardTitle></CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between"><span>Mode</span><span className="font-medium">{serviceMode === "MOBILE" ? "Mobile" : "In-bay"}</span></div>
          <div className="flex justify-between"><span>Total work</span><span className="font-medium">{totalMinutes}m</span></div>
          <div className="flex justify-between"><span>Add-ons</span><span className="font-medium">{addonIds.size}</span></div>
          <div className="flex justify-between border-t pt-2 text-base">
            <span className="font-medium">Total</span>
            <span className="font-semibold tabular-nums">{formatMoney(total, currency)}</span>
          </div>
          <div className="flex justify-between"><span>Vehicle</span><span className="font-medium">{selectedVehicleId ? vehicles.find((item) => item.id === selectedVehicleId)?.plate : vehicle.plate || "Not specified"}</span></div>
          <p className="text-xs text-muted-foreground">Travel time and minimum buffer are auto-computed for mobile bookings. Adjustments apply to the queued slot locks.</p>
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-3">
        <p className="text-xs text-muted-foreground">Idempotency: <span className="font-mono">{idemKey.slice(0, 8)}…</span></p>
        <Button type="submit" disabled={submitting}>{submitting ? "Booking…" : "Create booking"}</Button>
      </div>
    </form>
  );
}

const Field: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={`space-y-1.5 ${className ?? ""}`}>{children}</div>
);

const FieldLabel: React.FC<React.PropsWithChildren<{ optional?: boolean; className?: string }>> = ({ children, optional = false, className }) => (
  <Label className={`flex items-center justify-between gap-2 ${className ?? ""}`}>
    <span>{children}</span>
    <span className={optional ? "font-normal text-muted-foreground" : "text-[10px] uppercase tracking-wide text-primary"}>{optional ? "Optional" : "Required"}</span>
  </Label>
);

function parseBookingQuery(params: URLSearchParams) {
  const ids = (name: string, fallback?: string) => (params.get(name) ?? (fallback ? params.get(fallback) : null) ?? "")
    .split(",").map(Number).filter((id) => Number.isInteger(id) && id > 0);
  const positive = (name: string, fallback?: string) => ids(name, fallback)[0] ?? null;
  const mode = params.get("serviceMode") ?? params.get("mode");
  let embeddedCustomer: Partial<{ firstName: string; lastName: string; email: string; phone: string }> = {};
  try { embeddedCustomer = JSON.parse(params.get("customer") ?? "{}"); } catch { /* Ignore malformed optional prefills. */ }
  return {
    locationId: positive("locationId", "location"),
    resourceId: positive("resourceId", "resource"),
    staffResourceIds: ids("staffResourceIds", "resources"),
    serviceIds: ids("serviceIds", "services"),
    addonIds: ids("addonIds", "addons"),
    customerId: positive("customerId"),
    serviceMode: (mode === "MOBILE" ? "MOBILE" : "STATIONARY") as ServiceMode,
    date: params.get("date"),
    time: params.get("startAt") ?? params.get("time") ?? params.get("windowStart"),
    customer: {
      firstName: params.get("firstName") ?? embeddedCustomer.firstName ?? "",
      lastName: params.get("lastName") ?? embeddedCustomer.lastName ?? "",
      email: params.get("email") ?? embeddedCustomer.email ?? "",
      phone: params.get("phone") ?? embeddedCustomer.phone ?? "",
    },
    serviceAddress: {
      line1: params.get("addressLine1") ?? "", line2: params.get("addressLine2") ?? "",
      city: params.get("city") ?? "", region: params.get("region") ?? "",
      postalCode: params.get("postalCode") ?? "", countryCode: (params.get("countryCode") ?? "").toUpperCase(),
    },
  };
}

function formatSlotTime(startAt: string, timeZone?: string) {
  return new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", timeZone }).format(new Date(startAt));
}

function slotMatchesTime(startAt: string, requested: string, date: string, timeZone: string) {
  if (requested === startAt) return true;
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
    hourCycle: "h23", timeZone,
  }).formatToParts(new Date(startAt));
  const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}` === date && `${value.hour}:${value.minute}` === requested.slice(0, 5);
}
